import React from "react";
import { Box, LinearProgress, Stack, Typography } from "@mui/material";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";

import { FeatureContribution } from "../Types/MLAnalyticsTypes";

interface WhyThisRatingPanelProps {
  contributions: FeatureContribution[] | null | undefined;
}

/** SHAP-based "Why this rating?" explainability panel — top feature contributions. */
const WhyThisRatingPanel: React.FC<WhyThisRatingPanelProps> = ({ contributions }) => {
  const items = contributions || [];

  if (items.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        Not enough data was available to explain this prediction.
      </Typography>
    );
  }

  const maxAbsContribution = Math.max(...items.map((item) => Math.abs(item.contribution)), 0.0001);

  return (
    <Stack spacing={2}>
      {items.map((item, idx) => {
        const isPositive = item.direction === "positive";
        const barWidth = Math.min(100, (Math.abs(item.contribution) / maxAbsContribution) * 100);

        return (
          <Box key={`${item.feature}-${idx}`}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
              <Stack direction="row" spacing={1} alignItems="center">
                {isPositive ? (
                  <TrendingUpIcon fontSize="small" color="success" />
                ) : (
                  <TrendingDownIcon fontSize="small" color="error" />
                )}
                <Typography variant="body2" fontWeight={600}>
                  {item.label}
                </Typography>
              </Stack>
              <Typography
                variant="body2"
                fontWeight={700}
                color={isPositive ? "success.main" : "error.main"}
              >
                {isPositive ? "+" : ""}
                {item.contribution.toFixed(2)}
              </Typography>
            </Stack>
            <LinearProgress
              variant="determinate"
              value={barWidth}
              color={isPositive ? "success" : "error"}
              sx={{ height: 6, borderRadius: 3, mb: 0.5 }}
            />
            <Typography variant="caption" color="text.secondary">
              {item.description}
            </Typography>
          </Box>
        );
      })}
    </Stack>
  );
};

export default WhyThisRatingPanel;
